import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { useAssetComponents } from "@/hooks/useAssetComponents";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useCanvasId } from "@/utils/utils";
import { JsxElementType, JsxElement } from "@/utils/jsx/jsxElementTypes";

/**
 * Hook for navigating to story subcanvases from story files
 */
export const useStoryNavigation = () => {
  const setCanvasComponentKey = useCanvasStore(
    (state) => state.setCanvasComponentKey,
  );
  const { componentKeyToComponent } = useAssetComponents();
  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup,
  );
  const canvasId = useCanvasId();

  // Convert treeElementLookup to use relative paths for consistent comparison
  const relativePathTreeElementLookup = useMemo(() => {
    if (!treeElementLookup || !canvasId) return treeElementLookup;

    const converted: { [key: string]: JsxElement } = {};
    Object.keys(treeElementLookup).forEach((key) => {
      const element = treeElementLookup[key];
      if (element?.filename) {
        const pathParts = element.filename.split(canvasId);
        const relativePath =
          pathParts.length > 1
            ? pathParts[pathParts.length - 1].replace(/^\//, "")
            : element.filename;

        converted[key] = {
          ...element,
          filename: relativePath,
        };
      } else {
        converted[key] = element;
      }
    });

    return converted;
  }, [treeElementLookup, canvasId]);

  const isStoryFile = useCallback((filePath: string) => {
    if (!filePath) return false;
    return (
      (filePath.includes("/stories/") ||
        filePath.includes("\\stories\\") ||
        filePath.startsWith("stories/") ||
        filePath.startsWith("stories\\")) &&
      filePath.endsWith(".stories.tsx")
    );
  }, []);

  const navigateToStorySubcanvas = useCallback(
    (filePath: string) => {
      if (!componentKeyToComponent || !relativePathTreeElementLookup) {
        toast.error(
          "Component data not loaded yet. Please try again in a moment.",
        );
        return;
      }

      if (!isStoryFile(filePath)) {
        toast.error(`Not a valid story file: ${filePath}`);
        return;
      }

      const storyElement = Object.values(relativePathTreeElementLookup).find(
        (element) =>
          element?.filename === filePath &&
          element?.type === JsxElementType.StorybookComponent,
      );

      let componentName: string | undefined;

      if (storyElement?.storybookMainComponentImport?.name) {
        componentName = storyElement.storybookMainComponentImport.name;
      } else {
        const fileName = filePath.split(/[/\\]/).pop();
        componentName = fileName?.replace(".stories.tsx", "");
      }

      if (!componentName) {
        toast.error("Could not extract component name from story file");
        return;
      }

      const componentKey = Object.keys(componentKeyToComponent).find((key) => {
        const component = componentKeyToComponent[key];
        return component?.componentName === componentName;
      });

      if (componentKey) {
        setCanvasComponentKey(componentKey);
      } else {
        toast.error(`Component not found for ${componentName}.stories`);
      }
    },
    [
      componentKeyToComponent,
      setCanvasComponentKey,
      isStoryFile,
      relativePathTreeElementLookup,
    ],
  );

  return {
    isStoryFile,
    navigateToStorySubcanvas,
  };
};
